import {
    Engine,
    Scene,
    Label,
    Font,
    Color,
    Vector,
    vec,
    CollisionStartEvent,
    Rectangle,
    ScreenElement,
    TextAlign,
    Actor,
    BoundingBox,
    CoordPlane,
    Keys
} from "excalibur";
import { Macrophage } from "../actors/Macrophage";
import { Virus, EnemyType } from "../actors/Virus";
import { Antibody } from "../actors/Antibody";
import { Antigen } from "../actors/Antigen";
import { LipidRaft } from "../actors/LipidRaft";
import { CytokineStorm } from "../actors/CytokineStorm";
import { IgMPentamer } from "../actors/weapons/IgMPentamer";
import { PerforinGranule } from "../actors/weapons/PerforinGranule";
import { NeutrophilNet } from "../actors/weapons/NeutrophilNet";
import { ComplementBolt } from "../actors/weapons/ComplementBolt";
import { createWorldWalls } from "../actors/WorldWall";
import { VirusPool } from "../systems/VirusPool";
import { PHASES, TIMING, VISUALS, WEAPONS, SPAWNING, VIRUS, ITEMS } from "../config";
import { Resources } from "../resources";
import { AudioManager } from "../systems/AudioManager";

// Phase type
interface Phase {
    startTime: number;
    endTime: number;
    spawnInterval: number;
    virusSpeed: number;
    screenTint: { r: number; g: number; b: number; a: number } | null;
}

/**
 * GameScene - Main Gameplay Loop
 * 
 * The core gameplay scene where the immunological battle takes place.
 * Features a scrolling world with camera following the player.
 * Manages spawning, combat, progression, and victory conditions.
 */
export class GameScene extends Scene {
    private player!: Macrophage;
    private virusPool!: VirusPool;
    private background!: Actor;

    // HUD elements (screen-space, fixed position)
    private healthLabel!: Label;
    private xpLabel!: Label;
    private timerLabel!: Label;
    private virusCountLabel!: Label;
    private healthBar!: ScreenElement;
    private healthFill!: ScreenElement;
    private xpBar!: ScreenElement;
    private xpFill!: ScreenElement;
    private feverOverlay!: ScreenElement;
    private stormLabel!: Label;

    // Pause UI
    private pauseOverlay!: ScreenElement;
    private pauseLabel!: Label;
    private isPaused: boolean = false;

    // Cached HUD graphics to avoid creating new objects every frame
    private healthFillRect!: Rectangle;
    private xpFillRect!: Rectangle;
    private feverOverlayRect!: Rectangle;
    private lastHealthPercent: number = -1;
    private lastXpPercent: number = -1;
    private lastFeverTint: { r: number; g: number; b: number; a: number } | null = null;
    private lastScreenSize: { w: number; h: number } = { w: 0, h: 0 };

    // Timers
    private survivalTime: number = 0;
    private spawnTimer: number = 0;
    private weaponTimer: number = 0;
    private targetUpdateTimer: number = 0;
    private cytokineStormCooldown: number = 0;
    private lipidRaftSpawnTimer: number = 0;
    private readonly LIPID_RAFT_SPAWN_INTERVAL = 30000; // 30 seconds

    // Current target for antibodies
    private currentTarget: Virus | null = null;

    // Game state
    private isGameOver: boolean = false;
    private deathPenaltyAccumulated: number = 0;
    private resizeHandler: (() => void) | null = null;

    // Weapon Unlocks
    private hasIgM: boolean = false;
    private hasPerforin: boolean = false;
    private perforinTimer: number = 0;
    private readonly PERFORIN_COOLDOWN = 3000; // 3 seconds

    private hasNETs: boolean = false;
    private netTimer: number = 0;
    private readonly NET_COOLDOWN = 1000; // 1 second

    private hasComplement: boolean = false;
    private complementTimer: number = 0;
    private readonly COMPLEMENT_COOLDOWN = 4000; // 4 seconds

    private hasMemoryBCell: boolean = false; // Level 15 Capstone

    override onInitialize(engine: Engine): void {
        // Create background that covers the world
        this.createBackground();

        // Create the player (Macrophage) at world center
        this.player = new Macrophage();
        this.player.pos = vec(VISUALS.WORLD_WIDTH / 2, VISUALS.WORLD_HEIGHT / 2);
        this.add(this.player);

        // Set up camera to follow player
        this.camera.strategy.lockToActor(this.player);
        this.camera.strategy.limitCameraBounds(new BoundingBox(
            VISUALS.WIDTH / 2,
            VISUALS.HEIGHT / 2,
            VISUALS.WORLD_WIDTH - VISUALS.WIDTH / 2,
            VISUALS.WORLD_HEIGHT - VISUALS.HEIGHT / 2
        ));

        // Apply camera zoom to make everything appear bigger
        this.camera.zoom = VISUALS.CONTRAST.CAMERA_ZOOM;

        // Create the virus object pool
        this.virusPool = new VirusPool(this);
        this.virusPool.init();

        // Create invisible walls around world edges (only affects Macrophage)
        const walls = createWorldWalls();
        for (const wall of walls) {
            this.add(wall);
        }

        // Set up collision handling
        this.setupCollisions();

        // Create HUD (screen-space)
        this.createHUD();

        // Set up Cytokine Storm on double-tap/click
        this.setupCytokineStorm(engine);

        // Initial HUD layout
        this.updateHUDLayout(engine);
    }

    override onActivate(): void {
        // Listen for browser resize events
        this.resizeHandler = () => this.updateHUDLayout(this.engine);
        this.engine.screen.events.on('resize', this.resizeHandler);

        // Update layout immediately
        this.updateHUDLayout(this.engine);

        // Reset game state for a fresh start
        this.resetGame();

        // Start game music using AudioManager (respects volume settings)
        AudioManager.stopAllMusic();
        AudioManager.playGameMusic();
    }

    override onDeactivate(): void {
        // Cleanup listener
        if (this.resizeHandler) {
            this.engine.screen.events.off('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        // Stop music
        Resources.GameMusic.stop();
    }

    private createBackground(): void {
        // Get the background image dimensions - cache the sprite to reuse
        const bgImage = Resources.Background;
        const bgSprite = bgImage.toSprite(); // Create sprite once

        // Apply background brightness for better contrast with enemies
        bgSprite.opacity = VISUALS.CONTRAST.BACKGROUND_BRIGHTNESS;

        const bgWidth = bgSprite.width;
        const bgHeight = bgSprite.height;

        // Tile the background across the world
        const tilesX = Math.ceil(VISUALS.WORLD_WIDTH / bgWidth);
        const tilesY = Math.ceil(VISUALS.WORLD_HEIGHT / bgHeight);

        for (let tx = 0; tx < tilesX; tx++) {
            for (let ty = 0; ty < tilesY; ty++) {
                const tile = new Actor({
                    pos: vec(tx * bgWidth + bgWidth / 2, ty * bgHeight + bgHeight / 2),
                    anchor: Vector.Half,
                    z: -100 // Behind everything
                });
                // Reuse the same sprite for all tiles (don't create new ones)
                tile.graphics.use(bgSprite);
                this.add(tile);
            }
        }

        // Store reference to first tile for compatibility
        this.background = new Actor({
            pos: vec(VISUALS.WORLD_WIDTH / 2, VISUALS.WORLD_HEIGHT / 2),
            z: -101
        });
        this.add(this.background);
    }

    private createHUD(): void {
        const barWidth = 150;
        const barHeight = 16;
        const barX = 10;
        const barY = 20;
        const zoom = VISUALS.CONTRAST.CAMERA_ZOOM;
        const uiScale = vec(zoom, zoom);

        // Health bar background
        this.healthBar = new ScreenElement({
            pos: vec(barX, barY),
            width: barWidth,
            height: barHeight,
            anchor: Vector.Zero,
            scale: uiScale,
        });
        this.healthBar.graphics.use(new Rectangle({
            width: barWidth,
            height: barHeight,
            color: Color.fromHex('#333333'),
        }));
        this.add(this.healthBar);

        // Health bar fill
        this.healthFill = new ScreenElement({
            pos: vec(barX, barY),
            width: barWidth,
            height: barHeight,
            anchor: Vector.Zero,
            scale: uiScale,
        });
        this.healthFill.graphics.use(new Rectangle({
            width: barWidth,
            height: barHeight,
            color: Color.fromHex('#4ade80'),
        }));
        this.add(this.healthFill);

        // Health label
        this.healthLabel = new Label({
            text: 'MEMBRANE: 100/100',
            pos: vec(barX, barY + (barHeight + 6) * zoom), // Adjust Y offset for zoom
            font: new Font({
                family: 'Courier New, monospace',
                size: 14,
                color: Color.White,
            }),
            scale: uiScale,
        });
        this.healthLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.healthLabel);

        // XP bar background
        const xpBarY = barY + 40 * zoom; // Adjust vertical spacing
        this.xpBar = new ScreenElement({
            pos: vec(barX, xpBarY),
            width: barWidth,
            height: barHeight,
            anchor: Vector.Zero,
            scale: uiScale,
        });
        this.xpBar.graphics.use(new Rectangle({
            width: barWidth,
            height: barHeight,
            color: Color.fromHex('#333333'),
        }));
        this.add(this.xpBar);

        // XP bar fill
        this.xpFill = new ScreenElement({
            pos: vec(barX, xpBarY),
            width: 0,
            height: barHeight,
            anchor: Vector.Zero,
            scale: uiScale,
        });
        this.xpFill.graphics.use(new Rectangle({
            width: 0,
            height: barHeight,
            color: Color.fromHex('#fbbf24'),
        }));
        this.add(this.xpFill);

        // XP label
        this.xpLabel = new Label({
            text: 'LEVEL 1 - CLONAL EXPANSION',
            pos: vec(barX, xpBarY + (barHeight + 16) * zoom),
            font: new Font({
                family: 'Courier New, monospace',
                size: 14,
                color: Color.White,
            }),
            scale: uiScale,
        });
        this.xpLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.xpLabel);

        // Timer label
        this.timerLabel = new Label({
            text: 'FEVER: 5:00',
            pos: vec(0, 30), // Will be set by updateHUDLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 24,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Right,
            }),
            anchor: vec(1, 0.5),
            scale: uiScale,
        });
        this.timerLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.timerLabel);

        // Virus count label
        this.virusCountLabel = new Label({
            text: 'VIRAL LOAD: 0',
            pos: vec(0, 55), // Will be set by updateHUDLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 14,
                color: Color.White,
                textAlign: TextAlign.Right,
            }),
            anchor: vec(1, 0.5),
            scale: uiScale,
        });
        this.virusCountLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.virusCountLabel);

        // Cytokine Storm cooldown indicator
        this.stormLabel = new Label({
            text: 'CYTOKINE STORM: READY',
            pos: vec(0, 0), // Will be set by updateHUDLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 14,
                bold: true,
                color: Color.fromHex('#ff6b35'),
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
            scale: uiScale,
        });
        this.stormLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.stormLabel);

        // Fever overlay (hidden initially) - size will be set by updateHUDLayout
        this.feverOverlay = new ScreenElement({
            pos: vec(0, 0),
            width: 1, // Placeholder, will be updated
            height: 1,
            anchor: Vector.Zero,
            // Do NOT scale overlay, just set size to screen
            scale: vec(1, 1),
        });
        this.feverOverlay.graphics.use(new Rectangle({
            width: 1,
            height: 1,
            color: Color.fromRGB(180, 20, 20, 0),
        }));
        this.feverOverlay.z = 100; // Above everything
        this.add(this.feverOverlay);

        // Pause overlay (hidden initially)
        this.pauseOverlay = new ScreenElement({
            pos: vec(0, 0),
            width: 1,
            height: 1,
            anchor: Vector.Zero,
            z: 200,
        });
        this.pauseOverlay.graphics.use(new Rectangle({
            width: 1,
            height: 1,
            color: Color.fromRGB(0, 0, 0, 0.7),
        }));
        this.pauseOverlay.graphics.visible = false;
        this.add(this.pauseOverlay);

        // Pause label
        this.pauseLabel = new Label({
            text: 'PAUSED\n\nTap or press ESC to resume',
            pos: vec(0, 0), // Will be set by updateHUDLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 32,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
            z: 201,
        });
        this.pauseLabel.transform.coordPlane = CoordPlane.Screen;
        this.pauseLabel.graphics.visible = false;
        this.add(this.pauseLabel);

        // ESC key to pause (Desktop)
        this.engine.input.keyboard.on('press', (evt) => {
            if (this.engine.currentSceneName === 'game' && evt.key === Keys.Escape) {
                this.togglePause();
            }
            // Debug: K key to kill macrophage (set HP to 0)
            if (this.engine.currentSceneName === 'game' && evt.key === Keys.K) {
                this.player.hp = 0;
            }
        });

        // Tap on pause overlay to resume
        this.pauseOverlay.on('pointerdown', () => {
            if (this.isPaused) {
                this.togglePause();
            }
        });
    }

    /**
     * Update HUD element positions based on current screen size
     */
    private updateHUDLayout(engine: Engine): void {
        const zoom = this.camera.zoom;
        const w = engine.drawWidth * zoom; // Scale UP to counteract zoom shrinking
        const h = engine.drawHeight * zoom;

        // Timer in top-right
        this.timerLabel.pos = vec(w - 10 * zoom, 12 * zoom);

        // Virus count below timer
        this.virusCountLabel.pos = vec(w - 10 * zoom, 37 * zoom);

        // Cytokine Storm label at bottom center
        this.stormLabel.pos = vec(w / 2, h - 20 * zoom);

        // Pause overlay and label
        this.pauseOverlay.graphics.use(new Rectangle({
            width: w,
            height: h,
            color: Color.fromRGB(0, 0, 0, 0.7),
        }));
        this.pauseLabel.pos = vec(w / 2, h / 2);
    }

    /**
     * Toggle pause state
     */
    private togglePause(): void {
        this.isPaused = !this.isPaused;

        if (this.isPaused) {
            // Show pause UI
            this.pauseOverlay.graphics.visible = true;
            this.pauseLabel.graphics.visible = true;
        } else {
            // Hide pause UI
            this.pauseOverlay.graphics.visible = false;
            this.pauseLabel.graphics.visible = false;
        }
    }

    private setupCytokineStorm(engine: Engine): void {
        // Double-tap/click to trigger Cytokine Storm
        let lastTapTime = 0;
        const doubleTapThreshold = 300; // ms

        engine.input.pointers.primary.on('down', () => {
            const now = Date.now();
            if (now - lastTapTime < doubleTapThreshold) {
                this.triggerCytokineStorm();
            }
            lastTapTime = now;
        });
    }

    private triggerCytokineStorm(): void {
        if (this.cytokineStormCooldown > 0) return;

        // Trigger player shoot animation
        this.player.playShootAnimation();

        // Create the cytokine storm at player position (radius scales with level)
        const storm = new CytokineStorm(
            this.player.pos.x,
            this.player.pos.y,
            this.virusPool,
            this.player.level
        );
        this.add(storm);

        // Start cooldown
        this.cytokineStormCooldown = WEAPONS.CYTOKINE_STORM.COOLDOWN;

        // Handle viruses killed by storm via callback (ensures loot drops work)
        storm.onVirusKill = (v) => {
            if (v.isPoolActive()) {
                this.killVirus(v);
            }
        };
    }

    private setupCollisions(): void {
        // Player-Virus collision (player takes damage)
        this.player.on('collisionstart', (evt: CollisionStartEvent) => {
            if (evt.other instanceof Virus && evt.other.isPoolActive()) {
                this.player.takeDamage(evt.other.getDamage());
            }
        });
    }

    override onPreUpdate(engine: Engine, delta: number): void {
        if (this.isGameOver) return;
        if (this.isPaused) return; // Don't update while paused

        // Update Cytokine Storm cooldown
        if (this.cytokineStormCooldown > 0) {
            this.cytokineStormCooldown -= delta;
        }

        // Check for death
        if (this.player.isDead()) {
            this.handlePlayerDeath(engine);
            return;
        }

        // Update survival time
        this.survivalTime += delta;

        // Check for victory
        const effectiveTime = this.survivalTime - this.deathPenaltyAccumulated;
        if (effectiveTime >= TIMING.GAME_DURATION) {
            this.handleVictory(engine);
            return;
        }

        // Get current phase settings
        const phase = this.getCurrentPhase();

        // Spawn viruses
        this.spawnTimer += delta;
        if (this.spawnTimer >= phase.spawnInterval) {
            this.spawnTimer = 0;
            this.spawnVirus(phase.virusSpeed);
        }

        // Update targeting
        this.targetUpdateTimer += delta;
        if (this.targetUpdateTimer >= WEAPONS.ANTIBODY.TARGET_UPDATE_INTERVAL) {
            this.targetUpdateTimer = 0;
            this.updateTarget();
        }

        // Fire weapons
        this.weaponTimer += delta;
        const cooldown = Math.max(
            WEAPONS.ANTIBODY.MIN_COOLDOWN,
            WEAPONS.ANTIBODY.COOLDOWN - (this.player.level - 1) * 75
        );
        if (this.weaponTimer >= cooldown && this.currentTarget) {
            this.weaponTimer = 0;
            this.fireAntibody();
        }

        // Check antibody-virus collisions
        this.checkAntibodyCollisions();

        // Periodic Lipid Raft spawning (every 30 seconds)
        this.lipidRaftSpawnTimer += delta;
        if (this.lipidRaftSpawnTimer >= this.LIPID_RAFT_SPAWN_INTERVAL) {
            this.lipidRaftSpawnTimer = 0;
            this.spawnPeriodicLipidRaft();
        }


        // Check for Weapon Unlocks
        this.checkWeaponUnlocks();

        // Fire Perforin if unlocked
        if (this.hasPerforin) {
            this.perforinTimer += delta;

            // Apply cooldown reduction from Level 15 (Memory B-Cell)
            const cooldown = this.hasMemoryBCell ? this.PERFORIN_COOLDOWN * 0.5 : this.PERFORIN_COOLDOWN;

            if (this.perforinTimer >= cooldown) {
                this.perforinTimer = 0;
                this.firePerforin();
            }
        }

        // Spawn NETs if unlocked
        if (this.hasNETs) {
            this.netTimer += delta;
            const cooldown = this.hasMemoryBCell ? this.NET_COOLDOWN * 0.5 : this.NET_COOLDOWN;

            if (this.netTimer >= cooldown) {
                this.netTimer = 0;
                this.spawnNET();
            }
        }

        // Fire Complement if unlocked
        if (this.hasComplement) {
            this.complementTimer += delta;
            const cooldown = this.hasMemoryBCell ? this.COMPLEMENT_COOLDOWN * 0.5 : this.COMPLEMENT_COOLDOWN;

            if (this.complementTimer >= cooldown) {
                this.complementTimer = 0;
                this.fireComplement();
            }
        }

        // Update HUD
        this.updateHUD(phase);
    }

    private checkWeaponUnlocks(): void {
        // Level 3: IgM Pentamer
        if (this.player.level >= 3 && !this.hasIgM) {
            this.hasIgM = true;
            this.engine.clock.schedule(() => {
                // Spawn 2 orbiters
                const igm1 = new IgMPentamer(this.player, 0, (v) => this.killVirus(v));
                const igm2 = new IgMPentamer(this.player, Math.PI, (v) => this.killVirus(v));
                this.add(igm1);
                this.add(igm2);
                console.log("Unlocking IgM Pentamer (Dual Orbit)!");
            }, 100);
        }

        // Level 6: Perforin Granules
        if (this.player.level >= 6 && !this.hasPerforin) {
            this.hasPerforin = true;
            console.log("Unlocking Perforin Granules!");
        }

        // Level 9: Neutrophil NETs
        if (this.player.level >= 9 && !this.hasNETs) {
            this.hasNETs = true;
            console.log("Unlocking Neutrophil NETs!");
        }

        // Level 12: Complement Cascade
        if (this.player.level >= 12 && !this.hasComplement) {
            this.hasComplement = true;
            console.log("Unlocking Complement Cascade!");
        }

        // Level 15: Memory B-Cell (Capstone)
        if (this.player.level >= 15 && !this.hasMemoryBCell) {
            this.hasMemoryBCell = true;
            console.log("Unlocking Memory B-Cell Capstone!");
            // Verify visual cue?
        }
    }

    private fireComplement(): void {
        const viruses = this.virusPool.getActiveViruses();
        if (viruses.length === 0) return;

        // Find closest enemy
        let closestDist = Infinity;
        let closest: Virus | null = null;
        for (const v of viruses) {
            const d = v.pos.squareDistance(this.player.pos);
            if (d < closestDist) {
                closestDist = d;
                closest = v;
            }
        }

        if (closest) {
            ComplementBolt.fire(
                this.engine,
                this.player.pos,
                closest,
                (v) => this.killVirus(v)
            );
        }
    }

    private spawnNET(): void {
        const net = new NeutrophilNet(
            this.player.pos.x,
            this.player.pos.y,
            (v) => this.killVirus(v)
        );
        this.add(net);
        // Ensure NET is behind player
        net.z = this.player.z - 1;
    }

    private firePerforin(): void {
        // Find a random target (or closest group)
        const viruses = this.virusPool.getActiveViruses();
        if (viruses.length === 0) return;

        // Select random target for "mortar" feel
        const target = viruses[Math.floor(Math.random() * viruses.length)];

        // Fire from player to target
        const grenade = new PerforinGranule(
            this.player.pos.x,
            this.player.pos.y,
            target.pos.x,
            target.pos.y,
            (v) => this.killVirus(v) // Callback for kills
        );
        this.add(grenade);
    }

    private updateHUD(phase: Phase): void {
        const barWidth = 150;
        const barHeight = 16;

        // Health bar - only update if changed (avoid creating new Rectangle every frame)
        const healthPercent = Math.max(0, Math.min(1, this.player.hp / this.player.maxHp));
        const quantizedHealth = Math.round(healthPercent * 100) / 100; // Round to avoid micro-updates
        if (quantizedHealth !== this.lastHealthPercent) {
            this.lastHealthPercent = quantizedHealth;
            this.healthFillRect = new Rectangle({
                width: barWidth * healthPercent,
                height: barHeight,
                color: Color.fromHex(healthPercent > 0.3 ? '#4ade80' : '#ef4444'),
            });
            this.healthFill.graphics.use(this.healthFillRect);
        }
        this.healthLabel.text = `MEMBRANE: ${this.player.hp}/${this.player.maxHp}`;

        // XP bar - only update if changed
        const xpPercent = Math.max(0, Math.min(1, this.player.xp / this.player.xpToNextLevel));
        const quantizedXp = Math.round(xpPercent * 100) / 100;
        if (quantizedXp !== this.lastXpPercent) {
            this.lastXpPercent = quantizedXp;
            this.xpFillRect = new Rectangle({
                width: barWidth * xpPercent,
                height: barHeight,
                color: Color.fromHex('#fbbf24'),
            });
            this.xpFill.graphics.use(this.xpFillRect);
        }
        this.xpLabel.text = `LEVEL ${this.player.level} - CLONAL EXPANSION`;

        // Timer
        const effectiveTime = Math.max(0, this.survivalTime - this.deathPenaltyAccumulated);
        const remainingTime = Math.max(0, TIMING.GAME_DURATION - effectiveTime);
        const minutes = Math.floor(remainingTime / 60000);
        const seconds = Math.floor((remainingTime % 60000) / 1000);
        this.timerLabel.text = `FEVER: ${minutes}:${seconds.toString().padStart(2, '0')}`;

        // Virus count
        this.virusCountLabel.text = `VIRAL LOAD: ${this.virusPool.getActiveCount()}`;

        // Cytokine Storm status - cache the last state to avoid font recreation
        // Cytokine Storm status
        const stormReady = this.cytokineStormCooldown <= 0;
        if (stormReady) {
            this.stormLabel.text = 'CYTOKINE STORM: READY (Double-Tap)';

            // Only flash in Crisis phase
            const isCrisis = phase === PHASES.CRISIS as unknown as Phase;
            if (isCrisis) {
                // Flash effect (alternate every 150ms)
                const isFlash = Math.floor(Date.now() / 150) % 2 === 0;
                this.stormLabel.color = isFlash ? Color.fromHex('#ff6b35') : Color.White;
            } else {
                this.stormLabel.color = Color.fromHex('#ff6b35');
            }
        } else {
            const secondsLeft = Math.ceil(this.cytokineStormCooldown / 1000);
            this.stormLabel.text = `CYTOKINE STORM: ${secondsLeft}s`;
            this.stormLabel.color = Color.fromHex('#888888');
        }

        // Fever overlay - only update if phase tint or screen size changed
        const zoom = this.camera.zoom;
        const screenW = this.engine.drawWidth * zoom;
        const screenH = this.engine.drawHeight * zoom;
        const tint = phase.screenTint;
        const tintChanged = (tint?.r !== this.lastFeverTint?.r ||
            tint?.g !== this.lastFeverTint?.g ||
            tint?.b !== this.lastFeverTint?.b ||
            tint?.a !== this.lastFeverTint?.a);
        const sizeChanged = (screenW !== this.lastScreenSize.w || screenH !== this.lastScreenSize.h);

        if (tintChanged || sizeChanged) {
            this.lastFeverTint = tint ? { ...tint } : null;
            this.lastScreenSize = { w: screenW, h: screenH };

            if (tint) {
                this.feverOverlayRect = new Rectangle({
                    width: screenW,
                    height: screenH,
                    color: Color.fromRGB(tint.r, tint.g, tint.b, tint.a),
                });
            } else {
                this.feverOverlayRect = new Rectangle({
                    width: screenW,
                    height: screenH,
                    color: Color.fromRGB(0, 0, 0, 0),
                });
            }
            this.feverOverlay.graphics.use(this.feverOverlayRect);
        }
    }

    private getCurrentPhase(): Phase {
        const time = this.survivalTime;
        if (time < PHASES.PRODROMAL.endTime) {
            return PHASES.PRODROMAL as unknown as Phase;
        }
        if (time < PHASES.ACUTE.endTime) {
            return PHASES.ACUTE as unknown as Phase;
        }
        return PHASES.CRISIS as unknown as Phase;
    }

    private spawnVirus(speed: number): void {
        // Spawn viruses around the player in a ring, just outside the viewport
        const spawnDistance = Math.max(VISUALS.WIDTH, VISUALS.HEIGHT) / 2 + SPAWNING.SPAWN_MARGIN;
        const angle = Math.random() * Math.PI * 2;

        let x = this.player.pos.x + Math.cos(angle) * spawnDistance;
        let y = this.player.pos.y + Math.sin(angle) * spawnDistance;

        // Clamp to world bounds
        x = Math.max(SPAWNING.SPAWN_MARGIN, Math.min(VISUALS.WORLD_WIDTH - SPAWNING.SPAWN_MARGIN, x));
        y = Math.max(SPAWNING.SPAWN_MARGIN, Math.min(VISUALS.WORLD_HEIGHT - SPAWNING.SPAWN_MARGIN, y));

        // Select enemy type based on current phase
        const enemyType = this.selectEnemyType();
        this.virusPool.spawn(x, y, this.player, speed, enemyType);
    }

    /**
     * Select enemy type based on current game phase
     * - Prodromal: Only Influenza
     * - Acute: Influenza + Rhinovirus + occasional Adenovirus
     * - Crisis: Full mix with Mutated Strain bosses
     */
    private selectEnemyType(): EnemyType {
        const time = this.survivalTime;
        const roll = Math.random();

        if (time < PHASES.PRODROMAL.endTime) {
            // Prodromal: 100% Influenza
            return EnemyType.INFLUENZA;
        }

        if (time < PHASES.ACUTE.endTime) {
            // Acute: 50% Influenza, 35% Rhinovirus, 15% Adenovirus
            if (roll < 0.50) return EnemyType.INFLUENZA;
            if (roll < 0.85) return EnemyType.RHINOVIRUS;
            return EnemyType.ADENOVIRUS;
        }

        // Crisis: 30% Influenza, 40% Rhinovirus, 25% Adenovirus, 5% Mutated Strain
        if (roll < 0.30) return EnemyType.INFLUENZA;
        if (roll < 0.70) return EnemyType.RHINOVIRUS;
        if (roll < 0.95) return EnemyType.ADENOVIRUS;
        return EnemyType.MUTATED_STRAIN;
    }

    private updateTarget(): void {
        const viruses = this.virusPool.getActiveViruses();
        if (viruses.length === 0) {
            this.currentTarget = null;
            return;
        }

        let closestDist = Infinity;
        let closest: Virus | null = null;

        for (const virus of viruses) {
            const distSq = this.player.pos.squareDistance(virus.pos);
            if (distSq < closestDist) {
                closestDist = distSq;
                closest = virus;
            }
        }

        this.currentTarget = closest;
    }

    private fireAntibody(): void {
        if (!this.currentTarget) return;

        const antibody = new Antibody(
            this.player.pos.x,
            this.player.pos.y,
            this.currentTarget
        );
        this.add(antibody);
    }

    private checkAntibodyCollisions(): void {
        const antibodies = this.actors.filter(a => a instanceof Antibody) as Antibody[];
        const viruses = this.virusPool.getActiveViruses();

        for (const antibody of antibodies) {
            for (const virus of viruses) {
                const dist = antibody.pos.distance(virus.pos);
                if (dist < WEAPONS.ANTIBODY.RADIUS + VIRUS.RADIUS) {
                    // Apply damage to virus HP instead of instant kill
                    const killed = virus.takeDamage(WEAPONS.ANTIBODY.DAMAGE);
                    if (killed) {
                        this.killVirus(virus);
                    }
                    antibody.kill();
                    break;
                }
            }
        }
    }

    private killVirus(virus: Virus): void {
        // Spawn XP antigen
        const antigen = new Antigen(virus.pos.x, virus.pos.y, virus.getXPValue());
        antigen.setTarget(this.player);
        this.add(antigen);

        // Chance to spawn Lipid Raft health pickup
        if (Math.random() < ITEMS.LIPID_RAFT.SPAWN_CHANCE) {
            this.spawnLipidRaft(virus.pos.x, virus.pos.y);
        }

        this.virusPool.despawn(virus);
    }

    private spawnLipidRaft(x: number, y: number): void {
        const lipidRaft = new LipidRaft(x, y);
        lipidRaft.setTarget(this.player);
        this.add(lipidRaft);
    }

    /**
     * Spawn a periodic Lipid Raft near the player (every 30 seconds)
     */
    private spawnPeriodicLipidRaft(): void {
        // Spawn at random position near the player (within viewport range)
        const angle = Math.random() * Math.PI * 2;
        const distance = 150 + Math.random() * 100; // 150-250 pixels from player

        let x = this.player.pos.x + Math.cos(angle) * distance;
        let y = this.player.pos.y + Math.sin(angle) * distance;

        // Clamp to world bounds
        x = Math.max(50, Math.min(VISUALS.WORLD_WIDTH - 50, x));
        y = Math.max(50, Math.min(VISUALS.WORLD_HEIGHT - 50, y));

        this.spawnLipidRaft(x, y);
    }

    private handlePlayerDeath(engine: Engine): void {
        this.isGameOver = true;
        this.virusPool.clear();
        engine.goToScene('gameover');
    }

    private handleVictory(engine: Engine): void {
        this.isGameOver = true;
        this.virusPool.clear();
        engine.goToScene('victory');
    }

    private resetGame(): void {
        this.isGameOver = false;
        this.isPaused = false;
        this.survivalTime = 0;
        this.spawnTimer = 0;
        this.weaponTimer = 0;
        this.targetUpdateTimer = 0;
        this.cytokineStormCooldown = 0;
        this.lipidRaftSpawnTimer = 0;
        this.deathPenaltyAccumulated = 0;
        this.currentTarget = null;

        // Reset weapon unlocks
        this.hasIgM = false;
        this.hasPerforin = false;
        this.perforinTimer = 0;
        this.hasNETs = false;
        this.netTimer = 0;
        this.hasComplement = false;
        this.complementTimer = 0;
        this.hasMemoryBCell = false;

        // Hide pause overlay in case it was visible
        this.pauseOverlay.graphics.visible = false;
        this.pauseLabel.graphics.visible = false;

        if (this.player) {
            this.player.reset();
            // Reset player position to center of world
            this.player.pos = vec(VISUALS.WORLD_WIDTH / 2, VISUALS.WORLD_HEIGHT / 2);
        }

        if (this.virusPool) {
            this.virusPool.clear();
        }

        // Remove any lingering IgM orbiters or other actors
        for (const actor of this.actors) {
            if (actor.name === 'IgMPentamer' || actor.name === 'PerforinGranule' ||
                actor.name === 'NeutrophilNet' || actor.name === 'ComplementBolt' ||
                actor.name === 'CytokineStorm' || actor.name === 'Antibody' ||
                actor.name === 'Antigen' || actor.name === 'LipidRaft') {
                actor.kill();
            }
        }

        // Force HUD update
        this.updateHUD(this.getCurrentPhase());
    }
}
